import z from 'zod';
import { profileItemSchema } from './profile';
import { authStorage } from './auth';
import { get, post } from '../util/api';
import { adItemSchema } from './ad';
import { dataItem, dataArray, imageWithResize } from './helpers';

export const messageImageItemSchema = imageWithResize(
  z.object({
    '320_320': z.string(),
    '640_640': z.string(),
    '1280_1280': z.string(),
  })
);

const allowMediaOptions = z.enum(['undecided', 'yes', 'no']);

type AllowMediaOptionType = z.infer<typeof allowMediaOptions>;

export const messageItemSchema = z.object({
  id: z.number(),
  created: z.string().datetime({ offset: true }),
  seen_at: z.string().datetime({ offset: true }).nullable(),
  sender_id: z.number(),
  text: z.string(),
  allow_media: allowMediaOptions,
  ad_owner: dataItem(profileItemSchema),
  responder: dataItem(profileItemSchema),
  ad: dataItem(adItemSchema),
  image: dataItem(messageImageItemSchema).nullable(),
});

export type MessageItemType = z.infer<typeof messageItemSchema>;

export async function getConversations(): Promise<MessageItemType[]> {
  const auth = authStorage.get('auth', null);
  if (!auth) {
    return Promise.resolve([]);
  }

  const { data } = await get(
    `/account/conversations`,
    dataArray(messageItemSchema)
  );
  return data;
}

export async function getConversation({
  adId,
  responderId,
}: {
  adId: number;
  responderId: number;
}): Promise<MessageItemType[]> {
  const auth = authStorage.get('auth', null);
  if (!auth) {
    return Promise.resolve([]);
  }

  const { data } = await get(
    `/account/conversations/${adId}/${responderId}`,
    dataArray(messageItemSchema)
  );
  return data;
}

export async function addMessage({
  text,
  adId,
  responderId,
  image,
  onUploadProgress,
}: {
  text: string;
  adId: number;
  responderId: number;
  image?: File;
  onUploadProgress: (percentage: number) => void;
}) {
  return await post(
    `/messages/add`,
    z.object({
      id: z.number(),
    }),
    z.object({
      reason: z.enum(['unauthorized', 'ad_not_found', 'invalid_sender_id']),
    }),
    { text, ad_id: adId, responder_id: responderId, image },
    {},
    {
      onUploadProgress({ loaded, total }) {
        onUploadProgress(
          total && loaded ? Math.round((loaded * 100) / total) : 0
        );
      },
    }
  );
}

export async function allowMedia({
  message_ids,
  allow_media,
}: {
  message_ids: number[];
  allow_media: AllowMediaOptionType;
}) {
  return await post(
    `/messages/allow-media`,
    z.any(),
    z.object({
      reason: z.enum(['unauthorized', 'invalid_message_ids']),
    }),
    { message_ids, allow_media }
  );
}

export async function withdrawReport({ id, sender_id }: MessageItemType) {
  return await post(
    `/messages/withdraw-report`,
    z.any(),
    z.object({
      reason: z.enum(['unauthorized']),
    }),
    { message_id: id, reported_id: sender_id }
  );
}

export async function report({ id, sender_id }: MessageItemType) {
  return await post(
    `/messages/report`,
    z.any(),
    z.object({
      reason: z.enum(['unauthorized']),
    }),
    { message_id: id, reported_id: sender_id }
  );
}

export async function markAsSeen({
  adId,
  adOwnerId,
  responderId,
}: {
  adId: number;
  adOwnerId: number;
  responderId: number;
}) {
  return await post(
    `/messages/mark-as-seen`,
    z.object({
      updated: z.number(),
    }),
    z.object({
      reason: z.enum(['unauthorized']),
    }),
    { ad_id: adId, ad_owner_id: adOwnerId, responder_id: responderId }
  );
}
